import { useContext, useState } from 'react'
import Box from '@mui/joy/Box'
import Typography from '@mui/joy/Typography'
import Snackbar from '@mui/joy/Snackbar'
import { Loading } from '../components/ui/Loading'
import { Error } from '../components/ui/Error'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { SettingsAPI } from '../api/SettingsAPI'
import { Billing } from '../components/Billing'
import { OrganizationInfo } from '../components/OrganizationInfo'
import { SupportInfo } from '../components/SupportInfo'
import { ConnectedNetworks } from '../components/ConnectedNetworks'
import { SocialAuthButtons } from '../components/SocialAuthButtons'
import { UserInfo } from '../components/UserInfo'
import { NetworkAPI } from '../api/NetworkAPI'
import { ClerkUserInfo } from '../components/ClerkUserInfo'

export const Settings = () => {
    const queryClient = useQueryClient()
    const [snackColor, setSnackColor] = useState('danger')
    const [isSnackOpen, setIsSnackOpen] = useState(false)
    const [snackMsg, setSnackMsg] = useState(null)
    const { isPending, isError, data } = useQuery({
        queryKey: ['settings'],
        queryFn: SettingsAPI.getAll,
    })

    const showMsg = (message, color) => {
        setSnackColor(color)
        setSnackMsg(message)
        setIsSnackOpen(true)
    }

    const disconnectMutation = useMutation({
        mutationFn: (id) => {
            return NetworkAPI.deleteAccount(id)
        },
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ['settings'] })
            showMsg('Successfully disconnected network', 'success')
        },
        onError: (data, variables, context) => {
            showMsg('Error occurred disconnecting network', 'danger')
        },
    })

    const handleUserSubmit = (data) => {
        showMsg('Update successful', 'success')
    }

    const handleOrgSubmit = (data) => {
        console.log('Org Submit', data)
    }

    const disconnectNetwork = (network) => {
        showMsg('Disconnecting network...', 'danger')
        disconnectMutation.mutate(network.id)
    }

    const { mutate: connectAccount } = useMutation({
        mutationKey: ['connect-account'],
        mutationFn: NetworkAPI.connectAccount,
        onSuccess: ({ data }) => {
            window.location.href = data.authorization_url
        },
        onError: () => {
            const message =
                'Something went wrong while connecting your account. Please try again.'
            showMsg(message, 'danger')
        },
    })

    if (isPending) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Loading message={`fetching settings...`} />
            </Box>
        )
    }

    if (isError) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Error />
            </Box>
        )
    }

    return (
        <Box sx={{ m: 4, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.data.social_auth.available_networks.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography level="title-lg">
                        Connect New Account
                    </Typography>
                    <SocialAuthButtons
                        networks={data.data.social_auth.available_networks}
                        connectAccount={connectAccount}
                    />
                </Box>
            )}
            {data.data.social_auth.accounts.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography level="title-lg">Connected Accounts</Typography>
                    <ConnectedNetworks
                        accounts={data.data.social_auth.accounts}
                        onDisconnect={disconnectNetwork}
                    />
                </Box>
            )}
            {data.data.billing && <Billing data={data.data.billing} />}
            <ClerkUserInfo onSubmit={handleUserSubmit} />
            <OrganizationInfo
                data={data.data.organization}
                onSubmit={handleOrgSubmit}
            />
            <SupportInfo data={data.data.support_info} />
            <Snackbar
                variant="solid"
                color={snackColor}
                autoHideDuration={3000}
                onClose={() => setIsSnackOpen(false)}
                open={isSnackOpen}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                {snackMsg}
            </Snackbar>
        </Box>
    )
}
